// Filer och AI-granskning för en extern arbetsberedning.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  CheckCircle2,
  FileText,
  HelpCircle,
  Loader2,
  Sparkles,
  Trash2,
  Upload,
  XCircle,
} from "lucide-react";
import { useAuth } from "@/context/auth";

interface FileRow {
  id: string;
  file_path: string;
  file_name: string;
  mime_type: string | null;
  created_at: string;
}

interface Analysis {
  sammanfattning?: string;
  tacks?: string[];
  saknas?: string[];
  fortydliga?: { punkt: string; fraga: string }[];
  analyseradAt?: string;
  filer?: string[];
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  workPreparationId: string;
  workPreparationName: string;
  acknowledgedAt: string | null;
  onChanged?: () => void;
}

const ExternalWorkPrepPanel: React.FC<Props> = ({
  open,
  onOpenChange,
  projectId,
  workPreparationId,
  workPreparationName,
  acknowledgedAt,
  onChanged,
}) => {
  const { user } = useAuth();
  const inputRef = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<FileRow[]>([]);
  const [analysis, setAnalysis] = useState<Analysis | null>(null);
  const [busy, setBusy] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [ack, setAck] = useState<string | null>(acknowledgedAt);

  const load = useCallback(async () => {
    const [{ data: f }, { data: wp }] = await Promise.all([
      supabase
        .from("work_preparation_files")
        .select("id, file_path, file_name, mime_type, created_at")
        .eq("work_preparation_id", workPreparationId)
        .order("created_at", { ascending: true }),
      supabase
        .from("work_preparations")
        .select("data, external_ack_at")
        .eq("id", workPreparationId)
        .maybeSingle(),
    ]);
    setFiles((f ?? []) as FileRow[]);
    setAnalysis(((wp?.data as any)?.externalAnalysis ?? null) as Analysis | null);
    setAck(((wp as any)?.external_ack_at ?? null) as string | null);
  }, [workPreparationId]);

  useEffect(() => {
    if (open) void load();
  }, [open, load]);

  const upload = async (fileList: FileList | null) => {
    if (!fileList?.length) return;
    setBusy(true);
    for (const file of Array.from(fileList)) {
      const safe = file.name.replace(/[^\w.\-]+/g, "_");
      const path = `${projectId}/${workPreparationId}/${Date.now()}_${safe}`;
      const { error } = await supabase.storage.from("arbetsberedningar").upload(path, file, {
        upsert: false,
        contentType: file.type || undefined,
      });
      if (error) {
        toast.error(`Kunde inte ladda upp ${file.name}`);
        continue;
      }
      const { error: rowError } = await supabase.from("work_preparation_files").insert({
        work_preparation_id: workPreparationId,
        project_id: projectId,
        file_path: path,
        file_name: file.name,
        mime_type: file.type || null,
        file_size: file.size,
        uploaded_by: user?.id ?? null,
      } as any);
      if (rowError) toast.error(`Kunde inte spara ${file.name}`);
    }
    setBusy(false);
    if (inputRef.current) inputRef.current.value = "";
    await load();
    onChanged?.();
    toast.success("Filen är uppladdad");
  };

  const openFile = async (row: FileRow) => {
    const { data } = await supabase.storage
      .from("arbetsberedningar")
      .createSignedUrl(row.file_path, 300);
    if (data?.signedUrl) window.open(data.signedUrl, "_blank", "noopener");
    else toast.error("Kunde inte öppna filen");
  };

  const removeFile = async (row: FileRow) => {
    await supabase.storage.from("arbetsberedningar").remove([row.file_path]);
    await supabase.from("work_preparation_files").delete().eq("id", row.id);
    await load();
    onChanged?.();
  };

  const analyze = async () => {
    if (!files.length) return toast.error("Ladda upp beredningen först");
    setAnalyzing(true);
    const { data, error } = await supabase.functions.invoke("analyze-external-work-prep", {
      body: { workPreparationId },
    });
    setAnalyzing(false);
    if (error || (data as any)?.error) {
      return toast.error((data as any)?.error ?? "AI-granskningen misslyckades");
    }
    setAnalysis(data as Analysis);
    onChanged?.();
    toast.success("AI-granskningen är klar");
  };

  const toggleAck = async () => {
    const value = ack ? null : new Date().toISOString();
    const { error } = await supabase
      .from("work_preparations")
      .update({
        external_ack_at: value,
        external_ack_by: value ? user?.id ?? null : null,
        review_status: value ? "godkand" : "utkast",
      } as any)
      .eq("id", workPreparationId);
    if (error) return toast.error("Kunde inte spara kvittensen");
    setAck(value);
    onChanged?.();
  };

  const stale =
    analysis?.analyseradAt &&
    files.some((f) => new Date(f.created_at) > new Date(analysis.analyseradAt as string));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{workPreparationName}</DialogTitle>
          <DialogDescription>
            Extern arbetsberedning – ladda upp dokumentet och låt AI:n visa vad som täcks, saknas
            och behöver förtydligas. Godkännandet är alltid ditt som Bas-U.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Card>
            <CardContent className="space-y-2 p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Uppladdade filer</span>
                <Button size="sm" variant="outline" onClick={() => inputRef.current?.click()} disabled={busy}>
                  <Upload className="mr-1 h-4 w-4" /> Ladda upp
                </Button>
                <input
                  ref={inputRef}
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx,image/*"
                  className="hidden"
                  onChange={(e) => void upload(e.target.files)}
                />
              </div>
              {files.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  Ingen fil uppladdad ännu. PDF eller foto av pappersberedningen fungerar bäst.
                </p>
              )}
              {files.map((f) => (
                <div key={f.id} className="flex items-center justify-between gap-2 rounded border p-2 text-sm">
                  <button className="flex min-w-0 items-center gap-2 text-left" onClick={() => openFile(f)}>
                    <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <span className="truncate">{f.file_name}</span>
                    <span className="shrink-0 text-xs text-muted-foreground">
                      {new Date(f.created_at).toLocaleDateString("sv-SE")}
                    </span>
                  </button>
                  <Button variant="ghost" size="icon" onClick={() => removeFile(f)} title="Ta bort">
                    <Trash2 className="h-4 w-4 text-muted-foreground" />
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>

          <div className="flex flex-wrap items-center gap-2">
            <Button onClick={analyze} disabled={analyzing || !files.length}>
              {analyzing ? (
                <Loader2 className="mr-1 h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="mr-1 h-4 w-4" />
              )}
              {analyzing ? "Analyserar…" : "Analysera med AI"}
            </Button>
            <Button variant={ack ? "outline" : "default"} onClick={toggleAck}>
              {ack ? "Ta bort kvittens" : "Kvittera – inget mer behövs"}
            </Button>
            {ack && (
              <Badge className="bg-emerald-600 hover:bg-emerald-600">
                Kvitterad {new Date(ack).toLocaleDateString("sv-SE")}
              </Badge>
            )}
          </div>

          {stale && (
            <p className="text-sm text-amber-600">
              Nya filer har laddats upp efter senaste analysen – kör granskningen igen.
            </p>
          )}

          {analysis && (
            <Card>
              <CardContent className="space-y-4 p-4 text-sm">
                <div className="flex items-center justify-between">
                  <span className="font-medium">AI-granskning</span>
                  {analysis.analyseradAt && (
                    <span className="text-xs text-muted-foreground">
                      {new Date(analysis.analyseradAt).toLocaleString("sv-SE")}
                    </span>
                  )}
                </div>
                {analysis.sammanfattning && (
                  <p className="text-muted-foreground">{analysis.sammanfattning}</p>
                )}

                <div>
                  <div className="mb-1 flex items-center gap-1 font-medium text-emerald-700">
                    <CheckCircle2 className="h-4 w-4" /> Täcks
                  </div>
                  <ul className="list-disc space-y-1 pl-5">
                    {(analysis.tacks ?? []).map((t, i) => (
                      <li key={i}>{t}</li>
                    ))}
                    {!analysis.tacks?.length && <li className="text-muted-foreground">Inget noterat</li>}
                  </ul>
                </div>

                <div>
                  <div className="mb-1 flex items-center gap-1 font-medium text-destructive">
                    <XCircle className="h-4 w-4" /> Saknas
                  </div>
                  <ul className="list-disc space-y-1 pl-5">
                    {(analysis.saknas ?? []).map((t, i) => (
                      <li key={i}>{t}</li>
                    ))}
                    {!analysis.saknas?.length && <li className="text-muted-foreground">Inget saknas</li>}
                  </ul>
                </div>

                <div>
                  <div className="mb-1 flex items-center gap-1 font-medium text-amber-700">
                    <HelpCircle className="h-4 w-4" /> Behöver förtydligas
                  </div>
                  <ul className="space-y-2 pl-1">
                    {(analysis.fortydliga ?? []).map((f, i) => (
                      <li key={i} className="rounded border p-2">
                        <div className="font-medium">{f.punkt}</div>
                        <div className="text-muted-foreground">Fråga: {f.fraga}</div>
                      </li>
                    ))}
                    {!analysis.fortydliga?.length && (
                      <li className="text-muted-foreground">Inget behöver förtydligas</li>
                    )}
                  </ul>
                  {!!analysis.fortydliga?.length && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-2"
                      onClick={() => {
                        const text = (analysis.fortydliga ?? [])
                          .map((f, i) => `${i + 1}. ${f.punkt}: ${f.fraga}`)
                          .join("\n");
                        void navigator.clipboard.writeText(text);
                        toast.success("Frågorna kopierade – klistra in i mejl eller entreprenörslänk");
                      }}
                    >
                      Kopiera frågor till entreprenören
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ExternalWorkPrepPanel;
